import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOMServer from 'react-dom/server';

const { $ } = window;

function MultiSelectWithAvatar({
  name,
  value,
  label,
  dataUrl,
  valueMapUrl,
  selectOptions,
  placeholder,
  includeInactive,
  onIncludeInactiveChange,
  includeInactiveLabel,
  itemHeight
}) {
  const multiRef = useRef();
  const includeInactiveRef = useRef(includeInactive);
  const onIncludeInactiveChangeRef = useRef(onIncludeInactiveChange);
  const dataUrlRef = useRef(dataUrl);
  const valueMapUrlRef = useRef(valueMapUrl);

  useEffect(() => {
    includeInactiveRef.current = includeInactive;
  }, [includeInactive]);

  useEffect(() => {
    onIncludeInactiveChangeRef.current = onIncludeInactiveChange;
  }, [onIncludeInactiveChange]);

  useEffect(() => {
    valueMapUrlRef.current = valueMapUrl;
  }, [valueMapUrl]);

  const checkboxId = `include-inactive-cb-${name}`;

  const footerTemplate = onIncludeInactiveChange
    ? `<div class="mswa-include-inactive-footer"><label class="qmb-checkbox" for="${checkboxId}"><input type="checkbox" id="${checkboxId}"${
        includeInactive ? ' checked' : ''
      } class="mswa-include-inactive-cb"> <span class="qmb-checkbox-label">${
        includeInactiveLabel || 'Include inactive'
      }</span></label></div>`
    : undefined;

  useEffect(() => {
    $(multiRef.current).kendoMultiSelect({
      autoWidth: true,
      placeholder,
      dataTextField: 'label',
      dataValueField: 'value',
      change: selectOptions.onChange,
      multiple: true,
      value,
      height: 420,
      itemTemplate,
      tagTemplate,
      ...(footerTemplate && { footerTemplate }),
      virtual: {
        itemHeight,
        valueMapper(options) {
          $.ajax({
            url: valueMapUrlRef.current,
            type: 'GET',
            data: { ids: options.value }, // Send value to the server.
            success(data) {
              options.success(data); // Return the index number of the corresponding data item.
            }
          });
        }
      },
      dataSource: {
        transport: {
          read: {
            url: dataUrl,
            dataType: 'json'
          }
        },
        schema: {
          total: 'total_count',
          data: 'data'
        },
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        pageSize: 20
      }
    });

    const widget = $(multiRef.current).data('kendoMultiSelect');
    const syncIncludeInactiveCheckbox = () => {
      $(`#${checkboxId}`).prop('checked', Boolean(includeInactiveRef.current));
    };
    // dataSource.read() rebuilds footerTemplate from the init-time HTML (unchecked).
    // Re-apply checked after that bind so the box does not snap back.
    if (widget) {
      widget.bind('dataBound', syncIncludeInactiveCheckbox);
      widget.dataSource.bind('requestEnd', syncIncludeInactiveCheckbox);
    }

    let $body;
    if (onIncludeInactiveChange && widget) {
      $body = widget.wrapper.closest('body');
      // eslint-disable-next-line func-names
      $body.on(`change.include-inactive-${name}`, `#${checkboxId}`, function (e) {
        onIncludeInactiveChangeRef.current?.(e.target.checked);
      });
    }

    return () => {
      if ($body) {
        $body.off(`change.include-inactive-${name}`);
      }
      if (widget) {
        widget.unbind('dataBound', syncIncludeInactiveCheckbox);
        widget.dataSource.unbind('requestEnd', syncIncludeInactiveCheckbox);
        widget.destroy();
      }
    };
  }, []);

  // Keep the same Kendo instance when include_inactive is toggled. Remounting
  // (e.g. key={dataUrl}) leaves Kendo wrappers in the DOM; React then throws
  // removeChild NotFoundError and the deficiencies page goes blank.
  useEffect(() => {
    if (dataUrlRef.current === dataUrl) return;
    const widget = $(multiRef.current).data('kendoMultiSelect');
    if (!widget) return;
    if (widget.dataSource.transport?.options?.read) {
      widget.dataSource.transport.options.read.url = dataUrl;
    }
    if (widget.dataSource.options?.transport?.read) {
      widget.dataSource.options.transport.read.url = dataUrl;
    }
    dataUrlRef.current = dataUrl;
    widget.dataSource.read();
  }, [dataUrl]);

  useEffect(() => {
    const multiselect = $(multiRef.current).data('kendoMultiSelect');
    if (!multiselect) return;
    multiselect.value(value);
  }, [value]);

  useEffect(() => {
    $(`#${checkboxId}`).prop('checked', Boolean(includeInactive));
  }, [includeInactive, checkboxId]);

  const itemTemplate = ReactDOMServer.renderToString(
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {'# if (value === 0) { #'}
      <span
        className="qmb-avatar--32--unassigned-icon"
        style={{ display: 'flex', borderRadius: '50%', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fa-light fa-user-helmet-safety" />
      </span>
      {'# } else if (photoUrl) { #'}
      <span className="qmb-avatar--32">
        <img
          alt="#: label[0] #"
          style={{ borderRadius: '50%', border: '2px solid #: color #', boxSizing: 'border-box' }}
          src="#: photoUrl #"
        />
      </span>
      {'# } else { #'}
      <span className="qmb-avatar--32">
        <svg viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="8" style={{ fill: '#: color#' }} />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle">
            #: label[0] #
          </text>
        </svg>
      </span>
      {'# } #'}
      <div style={{ marginLeft: '0.8rem' }}>#: label #</div>
    </div>
  );

  const tagTemplate = ReactDOMServer.renderToString(
    <>
      {'# if (value === 0) {#'}
      <span className="qmb-avatar--24--unassigned--alt">
        <i className="fa-light fa-user-helmet-safety" />
      </span>
      {'# } else if (photoUrl) { #'}
      <span className="qmb-avatar--24">
        <img
          alt="#: label[0] #"
          style={{ borderRadius: '50%', border: '2px solid #: color #', boxSizing: 'border-box' }}
          src="#: photoUrl #"
        />
      </span>
      {'# } else { #'}
      <span className="qmb-avatar--24">
        <svg viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="8" style={{ fill: '#: color#' }} />
          <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle">
            #: label[0] #
          </text>
        </svg>
      </span>
      {'# } #'}
    </>
  );

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- pre-existing: label is associated via htmlFor/id */}
      <select id={name} name={name} onChange={() => {}} className="--avatars" ref={multiRef} multiple />
      <label className="qmb-label" htmlFor={name}>
        {label}
      </label>
    </>
  );
}

export default MultiSelectWithAvatar;

MultiSelectWithAvatar.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.array,
  label: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  selectOptions: PropTypes.object,
  jQueryEvents: PropTypes.shape({
    onChange: PropTypes.func,
    onClick: PropTypes.func,
    onKeyUp: PropTypes.func,
    onKeyDown: PropTypes.func,
    onFocus: PropTypes.func
  }),
  dataUrl: PropTypes.string,
  valueMapUrl: PropTypes.string,
  includeInactive: PropTypes.bool,
  onIncludeInactiveChange: PropTypes.func,
  includeInactiveLabel: PropTypes.string,
  itemHeight: PropTypes.number
};

MultiSelectWithAvatar.defaultProps = {
  value: undefined,
  label: undefined,
  placeholder: undefined,
  selectOptions: {},
  jQueryEvents: {},
  dataUrl: '',
  valueMapUrl: '',
  includeInactive: false,
  onIncludeInactiveChange: undefined,
  includeInactiveLabel: undefined,
  itemHeight: 90
};
